import type { GrandLivreVue, MouvementVue } from "../api/client";
import { formaterDate, formaterMontant, formaterNombre } from "../domaine/formats";

/** Solde présenté en valeur absolue, suivi de son sens. */
export function SoldeSigne({ valeur }: { valeur: number }) {
  return (
    <>
      {formaterMontant(Math.abs(valeur))}{" "}
      <span className="discret-texte">{valeur > 0 ? "D" : valeur < 0 ? "C" : ""}</span>
    </>
  );
}

/**
 * Comptes d'une page de grand livre, chacun dans sa carte.
 *
 * Un compte ouvert sur une page antérieure commence par sa ligne de report ;
 * un compte qui se poursuit sur la page suivante le dit au lieu d'afficher un
 * total qui ne serait que partiel. Chaque mouvement s'ouvre d'un clic ou de
 * la touche `Entrée` sur sa pièce.
 */
export function TableDuGrandLivre({
  livre,
  surMouvement,
  entete,
}: {
  livre: GrandLivreVue;
  surMouvement: (mouvement: MouvementVue, compte: string) => void;
  /** Complément du titre de chaque compte : un lien vers le lettrage, par exemple. */
  entete?: (compte: string) => React.ReactNode;
}) {
  return (
    <>
      {livre.comptes.map((compte) => (
        <div className="carte" key={compte.compte}>
          <h2>
            <span className="numero-compte">{compte.compte}</span>{" "}
            <span className="discret-texte">{compte.libelle}</span> {entete?.(compte.compte)}
          </h2>
          <div className="tableau-conteneur">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Journal</th>
                  <th className="nombre">N°</th>
                  <th>Libellé</th>
                  <th>Tiers</th>
                  <th>Lettre</th>
                  <th className="nombre">Débit</th>
                  <th className="nombre">Crédit</th>
                  <th className="nombre">Solde</th>
                </tr>
              </thead>
              <tbody>
                {compte.commenceAvant && (
                  <tr className="report">
                    <td colSpan={6}>Report des pages précédentes</td>
                    <td className="nombre">{formaterMontant(compte.reportDebit)}</td>
                    <td className="nombre">{formaterMontant(compte.reportCredit)}</td>
                    <td className="nombre">
                      <SoldeSigne valeur={compte.report} />
                    </td>
                  </tr>
                )}
                {compte.mouvements.map((mouvement) => (
                  <tr
                    key={`${mouvement.piece}-${mouvement.rang}`}
                    className={`cliquable${mouvement.statut === "BROUILLARD" ? " brouillard" : ""}`}
                    tabIndex={0}
                    title="Ouvrir la pièce"
                    onClick={() => surMouvement(mouvement, compte.compte)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        surMouvement(mouvement, compte.compte);
                      }
                    }}
                  >
                    <td>{formaterDate(mouvement.date)}</td>
                    <td>{mouvement.journal}</td>
                    <td className="nombre">{mouvement.numero ?? "—"}</td>
                    <td>
                      {mouvement.libelle}
                      {mouvement.libelle !== mouvement.libelleDePiece && (
                        <span className="discret-texte"> · {mouvement.libelleDePiece}</span>
                      )}
                    </td>
                    <td className="discret-texte">{mouvement.tiers ?? "—"}</td>
                    <td>{mouvement.lettre != null ? <span className="lettre">{mouvement.lettre}</span> : ""}</td>
                    <td className="nombre">{mouvement.debit > 0 ? formaterMontant(mouvement.debit) : ""}</td>
                    <td className="nombre">{mouvement.credit > 0 ? formaterMontant(mouvement.credit) : ""}</td>
                    <td className="nombre">{formaterMontant(mouvement.soldeCumule)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {compte.continueApres ? (
                  <tr className="report">
                    <td colSpan={9}>
                      Suite du compte à la page suivante —{" "}
                      {formaterNombre(compte.nombreDeMouvements)} mouvements au total, solde{" "}
                      <SoldeSigne valeur={compte.solde} />
                    </td>
                  </tr>
                ) : (
                  <tr className="total">
                    <td colSpan={6}>Total du compte</td>
                    <td className="nombre">{formaterMontant(compte.totalDebit)}</td>
                    <td className="nombre">{formaterMontant(compte.totalCredit)}</td>
                    <td className="nombre">
                      <SoldeSigne valeur={compte.solde} />
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

/** Navigation entre les pages d'un grand livre. */
export function Pagination({
  page,
  nombreDePages,
  surPage,
}: {
  page: number;
  nombreDePages: number;
  surPage: (page: number) => void;
}) {
  if (nombreDePages <= 1) return null;
  return (
    <nav className="pagination" aria-label="Pages">
      <button type="button" className="tertiaire" disabled={page === 0} onClick={() => surPage(0)}>
        « Première
      </button>
      <button type="button" className="tertiaire" disabled={page === 0} onClick={() => surPage(page - 1)}>
        ‹ Précédente
      </button>
      <span className="discret-texte">
        Page {formaterNombre(page + 1)} sur {formaterNombre(nombreDePages)}
      </span>
      <button
        type="button"
        className="tertiaire"
        disabled={page >= nombreDePages - 1}
        onClick={() => surPage(page + 1)}
      >
        Suivante ›
      </button>
      <button
        type="button"
        className="tertiaire"
        disabled={page >= nombreDePages - 1}
        onClick={() => surPage(nombreDePages - 1)}
      >
        Dernière »
      </button>
    </nav>
  );
}
